<template>
    <b-container class="bv-example-row">
        <b-row>
            <b-col>
                <b-alert show variant="light" class="alert alert-custom alert-white alert-shadow fade show gutter-b">
                    <div class="alert-icon">
                        <span class="svg-icon svg-icon-lg">
                            <inline-svg src="media/svg/icons/Tools/Compass.svg" />
                        </span>
                    </div>
                    <div class="alert-text">
                        <template v-if="current_device && current_device.version === 'soilblessv1'">
                            <strong>Soil Moisture Sensor</strong> — This is a low-power wireless sensor that periodically broadcasts moisture readings.
                            Updates depend on network coverage in the area and typically arrive every few minutes to hours.
                        </template>
                        <template v-else-if="current_device && current_device.version === 'templessv1'">
                            <strong>Temperature Sensor</strong> — This is a low-power wireless sensor that periodically broadcasts temperature readings.
                            Updates depend on network coverage in the area and typically arrive every few minutes to hours.
                        </template>
                        <template v-else>
                            You are seeing a sensor without internet connection, this device might look offline sometimes due to the sync coverage in the area.
                        </template>
                    </div>
                </b-alert>
            </b-col>
        </b-row>

        <!-- Current Reading + Moisture Scale (soil only) -->
        <b-row class="gutter-b" v-if="dataLoaded && current_device">
            <b-col md="4">
                <b-card class="card-custom card-stretch" no-body>
                    <b-card-body class="d-flex flex-column align-items-center justify-content-center py-5">
                        <div class="rounded-circle d-flex align-items-center justify-content-center mb-3"
                            :style="{ width: '80px', height: '80px', backgroundColor: currentReadingBgColor }">
                            <span class="font-weight-bolder text-white" style="font-size: 1.8rem;">
                                {{ currentReadingValue !== null ? currentReadingValue : '—' }}
                            </span>
                        </div>
                        <h5 class="font-weight-bold text-dark mb-1">{{ currentReadingLabel }}</h5>
                        <span class="text-muted font-size-sm" v-if="lastReadingTime">
                            Last reading: {{ lastReadingTime }}
                        </span>
                        <span class="text-muted font-size-sm" v-else>No readings yet</span>
                    </b-card-body>
                </b-card>
            </b-col>
            <b-col md="4" v-if="isSoilSensor">
                <b-card class="card-custom card-stretch" header-tag="header" no-body>
                    <template v-slot:header>
                        <div class="d-flex align-items-center w-100">
                            <h6 class="mb-0">Moisture Scale</h6>
                        </div>
                    </template>
                    <b-card-body class="py-3">
                        <div class="moisture-scale">
                            <div class="d-flex align-items-center mb-2">
                                <span class="moisture-dot" style="background: #1565C0;"></span>
                                <span class="ml-2 font-size-sm"><strong>0–3</strong> — Very wet / Submerged</span>
                            </div>
                            <div class="d-flex align-items-center mb-2">
                                <span class="moisture-dot" style="background: #42A5F5;"></span>
                                <span class="ml-2 font-size-sm"><strong>4–7</strong> — Wet / Well-watered</span>
                            </div>
                            <div class="d-flex align-items-center mb-2">
                                <span class="moisture-dot" style="background: #66BB6A;"></span>
                                <span class="ml-2 font-size-sm"><strong>8–11</strong> — Moist / Ideal range</span>
                            </div>
                            <div class="d-flex align-items-center mb-2">
                                <span class="moisture-dot" style="background: #FFA726;"></span>
                                <span class="ml-2 font-size-sm"><strong>12–15</strong> — Dry / Needs watering</span>
                            </div>
                            <div class="d-flex align-items-center">
                                <span class="moisture-dot" style="background: #EF5350;"></span>
                                <span class="ml-2 font-size-sm"><strong>16–19</strong> — Very dry / Critical</span>
                            </div>
                        </div>
                    </b-card-body>
                </b-card>
            </b-col>
            <b-col :md="isSoilSensor ? 4 : 4">
                <b-card class="card-custom card-stretch" header-tag="header" no-body>
                    <template v-slot:header>
                        <div class="d-flex align-items-center w-100">
                            <h6 class="mb-0">Sensor Timing</h6>
                        </div>
                    </template>
                    <b-card-body class="py-3">
                        <div class="d-flex align-items-center mb-3">
                            <b-icon icon="clock-history" variant="primary" font-scale="1.3" class="mr-3"></b-icon>
                            <div>
                                <span class="font-weight-bold d-block">Last Update</span>
                                <span class="text-muted font-size-sm">{{ lastReadingFull || 'No data yet' }}</span>
                            </div>
                        </div>
                        <div class="d-flex align-items-center mb-3">
                            <b-icon icon="arrow-repeat" variant="info" font-scale="1.3" class="mr-3"></b-icon>
                            <div>
                                <span class="font-weight-bold d-block">Avg. Update Frequency</span>
                                <span class="text-muted font-size-sm">{{ avgFrequency }}</span>
                            </div>
                        </div>
                        <div class="d-flex align-items-center">
                            <b-icon icon="graph-up" variant="success" font-scale="1.3" class="mr-3"></b-icon>
                            <div>
                                <span class="font-weight-bold d-block">Total Readings</span>
                                <span class="text-muted font-size-sm">{{ activeDataset.length }} readings recorded</span>
                            </div>
                        </div>
                    </b-card-body>
                </b-card>
            </b-col>
            <!-- Sensor Location Map (inline for temp sensors only — soil uses the insights toggle) -->
            <b-col md="4" v-if="!isSoilSensor && (sensorLocations.length > 0 || locationLoading)">
                <SuitchTileMap :locations="sensorLocations" />
            </b-col>
        </b-row>

        <!-- Watering Forecast + Gardening Insights (soil only) -->
        <b-row class="gutter-b" v-if="dataLoaded && isSoilSensor && wateringForecast">
            <b-col md="6">
                <b-card class="card-custom card-stretch" header-tag="header" no-body>
                    <template v-slot:header>
                        <div class="d-flex align-items-center w-100">
                            <h6 class="mb-0">
                                <b-icon icon="droplet-half" class="mr-1"></b-icon>
                                Watering Forecast
                            </h6>
                        </div>
                    </template>
                    <b-card-body class="py-3">
                        <div class="d-flex align-items-center mb-3">
                            <span class="font-size-h1 mr-3">{{ wateringForecast.trendIcon }}</span>
                            <div>
                                <span class="font-weight-bold d-block">{{ wateringForecast.trendLabel }}</span>
                                <span class="text-muted font-size-sm">{{ wateringForecast.rateDescription }}</span>
                            </div>
                        </div>

                        <!-- Time in each moisture state -->
                        <div class="mb-3" v-if="wateringForecast.timeInStates && wateringForecast.timeInStates.length > 0">
                            <span class="font-weight-bold font-size-sm d-block mb-2">Time observed in each state:</span>
                            <div v-for="(state, idx) in wateringForecast.timeInStates" :key="idx"
                                class="d-flex align-items-center mb-1 px-2 py-1 rounded"
                                :style="state.isCurrent && wateringForecast.timeInStates.length > 1 ? { border: '2px solid ' + state.color, background: state.color + '10' } : {}">
                                <span v-if="state.isCurrent && wateringForecast.timeInStates.length > 1" class="font-weight-bold mr-1" :style="{ color: state.color, fontSize: '11px' }">▸</span>
                                <span class="moisture-dot mr-2" :style="{ background: state.color }"></span>
                                <span class="font-size-sm" :class="{ 'font-weight-bold': state.isCurrent && wateringForecast.timeInStates.length > 1 }" style="min-width: 70px;">{{ state.label }}</span>
                                <div class="flex-grow-1 mx-2" style="height: 8px; background: #eee; border-radius: 4px; overflow: hidden;">
                                    <div :style="{ width: state.pct + '%', height: '100%', background: state.color, borderRadius: '4px' }"></div>
                                </div>
                                <span class="text-muted font-size-xs" style="min-width: 60px; text-align: right;">{{ state.duration }}</span>
                                <span v-if="state.isCurrent && wateringForecast.timeInStates.length > 1" class="badge ml-2 text-white font-size-xs" :style="{ background: state.color }">current</span>
                            </div>
                        </div>

                        <div v-if="wateringForecast.alreadyCritical" class="bg-light-danger rounded p-3 mb-2">
                            <b-icon icon="exclamation-circle" variant="danger" class="mr-2"></b-icon>
                            <span class="font-weight-bold">Very dry for {{ wateringForecast.alreadyCritical }}.</span>
                            <span class="ml-1">Soil is critically dry — water immediately to prevent root damage and plant stress.</span>
                        </div>
                        <div v-else-if="wateringForecast.alreadyDry" class="bg-light-warning rounded p-3 mb-2">
                            <b-icon icon="exclamation-triangle" variant="warning" class="mr-2"></b-icon>
                            <span class="font-weight-bold">Dry for {{ wateringForecast.alreadyDry }}.</span>
                            <span class="ml-1">Most plants need watering after a few hours in this state — consider watering soon.</span>
                        </div>
                        <div v-else-if="wateringForecast.wateringEta" class="bg-light-warning rounded p-3 mb-2">
                            <b-icon icon="exclamation-triangle" variant="warning" class="mr-2"></b-icon>
                            <span class="font-weight-bold">Watering recommended in:</span>
                            <span class="ml-1">{{ wateringForecast.wateringEta }}</span>
                        </div>
                        <div v-if="wateringForecast.criticalEta && !wateringForecast.alreadyCritical" class="bg-light-danger rounded p-3 mb-2">
                            <b-icon icon="exclamation-circle" variant="danger" class="mr-2"></b-icon>
                            <span class="font-weight-bold">Critical dryness in:</span>
                            <span class="ml-1">{{ wateringForecast.criticalEta }}</span>
                        </div>
                        <div v-if="!wateringForecast.wateringEta && !wateringForecast.criticalEta && !wateringForecast.alreadyDry && !wateringForecast.alreadyCritical && wateringForecast.currentState !== 'Dry' && wateringForecast.currentState !== 'Very dry'" class="bg-light-success rounded p-3 mb-2">
                            <b-icon icon="check-circle" variant="success" class="mr-2"></b-icon>
                            <span>No watering needed based on current trend</span>
                        </div>

                        <!-- Detected watering events -->
                        <div v-if="wateringForecast.wateringEvents && wateringForecast.wateringEvents.count > 0" class="mt-3">
                            <span class="font-weight-bold font-size-sm d-block mb-2">
                                <b-icon icon="droplet" variant="primary" class="mr-1"></b-icon>
                                {{ wateringForecast.wateringEvents.count }} watering event{{ wateringForecast.wateringEvents.count > 1 ? 's' : '' }} detected
                                <span v-if="wateringForecast.wateringEvents.avgInterval" class="text-muted font-weight-normal">
                                    — avg. every {{ wateringForecast.wateringEvents.avgInterval }}
                                </span>
                            </span>
                            <div v-for="(evt, idx) in wateringForecast.wateringEvents.list.slice(0, 5)" :key="'we'+idx"
                                class="d-flex align-items-center font-size-sm mb-1 pl-2"
                                style="border-left: 3px solid #42A5F5;">
                                <span class="text-muted" style="min-width: 120px;">{{ evt.date }}</span>
                                <span class="ml-2">
                                    {{ evt.fromState }} ({{ evt.from }}) → {{ evt.toState }} ({{ evt.to }})
                                    <span class="text-primary font-weight-bold ml-1">↓{{ evt.drop }}</span>
                                </span>
                                <span v-if="evt.effectDuration" class="text-muted ml-2 font-size-xs">
                                    lasted {{ evt.effectDuration }}
                                </span>
                            </div>
                            <small v-if="wateringForecast.wateringEvents.count > 5" class="text-muted pl-2">
                                + {{ wateringForecast.wateringEvents.count - 5 }} more events
                            </small>

                            <!-- Watering analysis tips -->
                            <div v-if="wateringForecast.wateringEvents.tips && wateringForecast.wateringEvents.tips.length > 0" class="mt-3">
                                <div v-for="(tip, tidx) in wateringForecast.wateringEvents.tips" :key="'tip'+tidx"
                                    class="d-flex align-items-start font-size-sm mb-2 bg-light rounded p-2">
                                    <span class="mr-2 flex-shrink-0">{{ tip.icon }}</span>
                                    <span>{{ tip.text }}</span>
                                </div>
                            </div>
                        </div>

                        <small class="text-muted d-block mt-2">
                            Based on {{ wateringForecast.sampleCount }} readings over {{ wateringForecast.timeSpan }}. Estimates may vary with weather.
                        </small>
                    </b-card-body>
                </b-card>
            </b-col>
            <b-col md="6">
                <b-card class="card-custom card-stretch" header-tag="header" no-body>
                    <template v-slot:header>
                        <div class="d-flex align-items-center justify-content-between w-100">
                            <h6 class="mb-0">
                                <b-icon :icon="insightsView === 'insights' ? 'flower1' : 'geo-alt'" class="mr-1"></b-icon>
                                {{ insightsView === 'insights' ? 'Gardening Insights' : 'Sensor Location' }}
                            </h6>
                            <b-button-group size="sm" v-if="sensorLocations.length > 0 || locationLoading">
                                <b-button :variant="insightsView === 'insights' ? 'primary' : 'outline-primary'" @click="insightsView = 'insights'">
                                    <b-icon icon="flower1" class="mr-1"></b-icon>Insights
                                </b-button>
                                <b-button :variant="insightsView === 'map' ? 'primary' : 'outline-primary'" @click="insightsView = 'map'">
                                    <b-icon icon="geo-alt" class="mr-1"></b-icon>Map
                                    <b-spinner v-if="locationLoading" small class="ml-1"></b-spinner>
                                </b-button>
                            </b-button-group>
                        </div>
                    </template>
                    <!-- Gardening Insights view -->
                    <b-card-body class="py-3" v-if="insightsView === 'insights'">
                        <div v-for="(insight, idx) in gardeningInsights" :key="idx" class="d-flex align-items-start mb-3">
                            <span class="font-size-lg mr-3 flex-shrink-0">{{ insight.icon }}</span>
                            <div>
                                <span class="font-weight-bold d-block">{{ insight.title }}</span>
                                <span class="text-muted font-size-sm">{{ insight.description }}</span>
                            </div>
                        </div>
                    </b-card-body>
                    <!-- Sensor Location Map view -->
                    <b-card-body class="p-0" v-else>
                        <SuitchTileMap :locations="sensorLocations" :embedded="true" />
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>

        <b-row>
            <b-col>
                <SuitchBasicChart :device="current_device" :dataset="markers" prop="line-chart" :labelOfdata="labelOfData" :dataLoaded="dataLoaded" :yaxisMax="isSoilSensor ? 19 : null" :moistureMode="isSoilSensor" @range-data="onRangeData" />
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <SuitchBatteryWidget :device="current_device" :limitBatteryDate="limitBatteryDate"/>
                <br>
                <SuitchDataDetailsWidget :device="current_device" :dataset="markers" :dataLoaded="dataLoaded"/>

            </b-col>
            <b-col>
                <SuitchTrackerStatusWidget :device="current_device" findMyType="soil" :dataLoaded="dataLoaded"/>
                <br />
                
                <br />
                <SuitchDetailsWidget :device="current_device" />
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="6"> </b-col>
            <b-col cols="4">
                <!-- more information here -->
            </b-col>
        </b-row>
    </b-container>

    <!--end::Example-->
</template>

<style scoped>
.moisture-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
}
</style>

<script>
import {
    FETCH_SINGLE_DEVICE,
    GET_FINDMY_SOIL_SENSOR,
    GET_FINDMY_LOCATION,
} from "@/core/services/store/devices.module";
import SuitchTrackerStatusWidget from "@/view/content/widgets/suitch/suitch-tracker-status-widget.vue";
import SuitchBatteryWidget from "@/view/content/widgets/suitch/suitch-battery-widget.vue";
import SuitchDataDetailsWidget from "@/view/content/widgets/suitch/suitch-raw-details-widget.vue";
import SuitchDetailsWidget from "@/view/content/widgets/suitch/suitch-details-widget.vue";
import SuitchBasicChart from "@/view/content/widgets/suitch/suitch-chart-generic-widget.vue";
import SuitchTileMap from "@/view/content/widgets/suitch/suitch-tile-map-widget.vue";


export default {
    data() {
        return {
            limitBatteryDate:"",
            global_wizard: {},
            current_device: null,
            markers: [],
            activeDataset: [],
            labelOfData: '',
            dataLoaded: null,
            sensorLocations: [],
            locationLoading: false,
            insightsView: 'insights',
        };
    },

    computed: {
        isSoilSensor() {
            return this.current_device && this.current_device.version === 'soilblessv1';
        },
        currentReadingValue() {
            if (this.activeDataset.length === 0) return null;
            const latest = this.activeDataset[0];
            return Number(latest.value);
        },
        currentReadingLabel() {
            if (this.currentReadingValue === null) return 'No Data';
            if (!this.isSoilSensor) return this.currentReadingValue + '°C';
            const v = this.currentReadingValue;
            if (v <= 3) return 'Very Wet';
            if (v <= 7) return 'Wet';
            if (v <= 11) return 'Moist';
            if (v <= 15) return 'Dry';
            return 'Very Dry';
        },
        currentReadingBgColor() {
            if (this.currentReadingValue === null) return '#B0BEC5';
            if (!this.isSoilSensor) {
                const t = this.currentReadingValue;
                if (t <= 10) return '#42A5F5';
                if (t <= 25) return '#66BB6A';
                if (t <= 35) return '#FFA726';
                return '#EF5350';
            }
            const v = this.currentReadingValue;
            if (v <= 3) return '#1565C0';
            if (v <= 7) return '#42A5F5';
            if (v <= 11) return '#66BB6A';
            if (v <= 15) return '#FFA726';
            return '#EF5350';
        },
        lastReadingTime() {
            if (this.activeDataset.length === 0) return null;
            const latest = this.activeDataset[0];
            return this.getRelativeTime(latest.created_at);
        },
        lastReadingFull() {
            if (this.activeDataset.length === 0) return null;
            const latest = this.activeDataset[0];
            return this.formatDate(latest.created_at);
        },
        avgFrequency() {
            if (this.activeDataset.length < 2) return 'Not enough data';
            const first = new Date(this.activeDataset[0].created_at);
            const lastM = new Date(this.activeDataset[this.activeDataset.length - 1].created_at);
            const totalMs = Math.abs(lastM - first);
            const avgMs = totalMs / (this.activeDataset.length - 1);
            const avgMin = Math.round(avgMs / 60000);
            if (avgMin < 60) return '~' + avgMin + ' minutes between readings';
            const avgHours = Math.round(avgMin / 60);
            if (avgHours < 24) return '~' + avgHours + ' hours between readings';
            const avgDays = Math.round(avgHours / 24);
            return '~' + avgDays + ' days between readings';
        },
        wateringForecast() {
            if (!this.isSoilSensor || this.activeDataset.length < 2) return null;

            // Use ALL available readings. activeDataset[0] is newest.
            var all = this.activeDataset;
            var sampleCount = all.length;

            // Build chronological points (oldest first)
            var points = all.map(function (m) {
                return { time: new Date(m.created_at).getTime(), value: Number(m.value) };
            }).slice().reverse();

            // --- Time span ---
            var totalMs = points[points.length - 1].time - points[0].time;

            // --- Time in each moisture state ---
            var stateMap = {
                'Very wet': { min: 0, max: 3, color: '#1565C0', ms: 0 },
                'Wet':      { min: 4, max: 7, color: '#42A5F5', ms: 0 },
                'Ideal':    { min: 8, max: 11, color: '#66BB6A', ms: 0 },
                'Dry':      { min: 12, max: 15, color: '#FFA726', ms: 0 },
                'Very dry': { min: 16, max: 19, color: '#EF5350', ms: 0 },
            };
            var stateOrder = ['Very wet', 'Wet', 'Ideal', 'Dry', 'Very dry'];

            function getState(v) {
                if (v <= 3) return 'Very wet';
                if (v <= 7) return 'Wet';
                if (v <= 11) return 'Ideal';
                if (v <= 15) return 'Dry';
                return 'Very dry';
            }

            for (var i = 0; i < points.length - 1; i++) {
                var seg = points[i + 1].time - points[i].time;
                var st = getState(points[i].value);
                stateMap[st].ms += seg;
            }
            // Also add time from last point to now
            var nowMs = Date.now();
            var lastState = getState(points[points.length - 1].value);
            stateMap[lastState].ms += (nowMs - points[points.length - 1].time);
            var totalStateMs = 0;
            stateOrder.forEach(function (k) { totalStateMs += stateMap[k].ms; });

            var timeInStates = [];
            var currentState = getState(Number(this.activeDataset[0].value));
            stateOrder.forEach(function (k) {
                if (stateMap[k].ms > 0) {
                    var pct = totalStateMs > 0 ? Math.round((stateMap[k].ms / totalStateMs) * 100) : 0;
                    timeInStates.push({
                        label: k,
                        color: stateMap[k].color,
                        pct: pct,
                        duration: formatDuration(stateMap[k].ms),
                        isCurrent: k === currentState,
                    });
                }
            });

            // --- Linear regression on ALL points ---
            var n = points.length;
            var sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
            for (var j = 0; j < n; j++) {
                sumX += points[j].time;
                sumY += points[j].value;
                sumXY += points[j].time * points[j].value;
                sumXX += points[j].time * points[j].time;
            }
            var denom = n * sumXX - sumX * sumX;
            if (denom === 0) return null;
            var slope = (n * sumXY - sumX * sumY) / denom;
            var slopePerHour = slope * 3600000;

            var current = Number(this.activeDataset[0].value);
            var trend, trendLabel, trendIcon, rateDescription;

            if (Math.abs(slopePerHour) < 0.01) {
                trend = 'stable';
                trendLabel = 'Moisture is stable';
                trendIcon = '⚖️';
                rateDescription = 'No significant change detected across ' + sampleCount + ' readings.';
            } else if (slopePerHour > 0) {
                trend = 'drying';
                trendLabel = 'Soil is drying';
                trendIcon = '☀️';
                rateDescription = 'Drying at ~' + Math.abs(slopePerHour).toFixed(2) + ' levels/hour. Sun exposure and evaporation are likely driving moisture loss.';
            } else {
                trend = 'wetting';
                trendLabel = 'Soil is getting wetter';
                trendIcon = '💧';
                rateDescription = 'Wetting at ~' + Math.abs(slopePerHour).toFixed(2) + ' levels/hour. Likely from recent watering or rain.';
            }

            var wateringEta = null;
            var criticalEta = null;
            var alreadyDry = null;
            var alreadyCritical = null;

            // Currently already in a dry state — compute how long it's been dry
            if (currentState === 'Dry' || currentState === 'Very dry') {
                // Walk backwards from newest to find when it entered this state
                var newestPoints = points.slice().reverse(); // newest first
                var dryStartMs = newestPoints[0].time;
                var allReadingsDry = true;
                for (var k = 1; k < newestPoints.length; k++) {
                    var kState = getState(newestPoints[k].value);
                    if (kState !== 'Dry' && kState !== 'Very dry') {
                        allReadingsDry = false;
                        break;
                    }
                    dryStartMs = newestPoints[k].time;
                }
                var dryDurationMs = nowMs - dryStartMs;
                var dryPrefix = allReadingsDry ? 'at least ' : '';
                if (currentState === 'Very dry') {
                    alreadyCritical = dryPrefix + formatDuration(dryDurationMs);
                } else {
                    alreadyDry = dryPrefix + formatDuration(dryDurationMs);
                    // Still estimate when it could become critical if drying
                    if (trend === 'drying' && current < 16) {
                        criticalEta = this.formatHoursEta((16 - current) / slopePerHour);
                    }
                }
            } else if (trend === 'drying' && current < 12) {
                wateringEta = this.formatHoursEta((12 - current) / slopePerHour);
            }
            if (trend === 'drying' && current < 16 && !alreadyCritical && !alreadyDry) {
                criticalEta = this.formatHoursEta((16 - current) / slopePerHour);
            }

            return {
                trend: trend,
                trendLabel: trendLabel,
                trendIcon: trendIcon,
                rateDescription: rateDescription,
                wateringEta: wateringEta,
                criticalEta: criticalEta,
                alreadyDry: alreadyDry,
                alreadyCritical: alreadyCritical,
                currentState: currentState,
                sampleCount: sampleCount,
                timeSpan: formatDuration(totalMs),
                timeInStates: timeInStates,
                wateringEvents: detectWateringEvents(points, formatDuration),
            };

            function formatDuration(ms) {
                var mins = Math.round(ms / 60000);
                if (mins < 60) return mins + 'min';
                var hrs = Math.floor(mins / 60);
                var remMin = mins % 60;
                if (hrs < 24) return hrs + 'h ' + remMin + 'min';
                var days = Math.floor(hrs / 24);
                var remHrs = hrs % 24;
                if (days === 1) return '1 day ' + remHrs + 'h';
                return days + ' days ' + remHrs + 'h';
            }

            function detectWateringEvents(pts, fmtDur) {
                // A watering event = sudden drop of 3+ levels (drier → wetter)
                // between consecutive readings.
                var events = [];
                for (var w = 1; w < pts.length; w++) {
                    var drop = pts[w - 1].value - pts[w].value;
                    if (drop >= 3) {
                        var evtDate = new Date(pts[w].time);
                        var options = { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' };
                        var dateStr;
                        try {
                            dateStr = new Intl.DateTimeFormat(undefined, options).format(evtDate);
                        } catch (e) {
                            dateStr = evtDate.toLocaleString();
                        }
                        // Measure how long the wet effect lasted before drying back
                        var effectDuration = null;
                        var lowestAfter = pts[w].value;
                        for (var a = w + 1; a < pts.length; a++) {
                            if (pts[a].value >= pts[w - 1].value) {
                                // Dried back to pre-watering level
                                effectDuration = fmtDur(pts[a].time - pts[w].time);
                                break;
                            }
                            if (pts[a].value < lowestAfter) lowestAfter = pts[a].value;
                        }
                        events.push({
                            date: dateStr,
                            time: pts[w].time,
                            from: pts[w - 1].value,
                            to: pts[w].value,
                            drop: drop,
                            fromState: getState(pts[w - 1].value),
                            toState: getState(pts[w].value),
                            effectDuration: effectDuration,
                            lowestAfter: lowestAfter,
                        });
                    }
                }

                // Compute average time between watering events
                var avgInterval = null;
                if (events.length >= 2) {
                    var evtTimestamps = events.map(function (e) { return e.time; });
                    var totalInterval = evtTimestamps[evtTimestamps.length - 1] - evtTimestamps[0];
                    avgInterval = fmtDur(totalInterval / (evtTimestamps.length - 1));
                }

                // Generate watering analysis tips
                var tips = [];
                if (events.length > 0) {
                    // Check if any watering brought soil too wet
                    var overwateredEvents = events.filter(function (e) { return e.to <= 3; });
                    if (overwateredEvents.length > 0) {
                        tips.push({
                            icon: '💦',
                            text: overwateredEvents.length + ' watering' + (overwateredEvents.length > 1 ? 's' : '') + ' brought soil to "Very wet" — consider reducing the amount to avoid waterlogging.'
                        });
                    }
                    // Check how many had a measurable effect duration
                    var withEffect = events.filter(function (e) { return e.effectDuration != null; });
                    if (withEffect.length > 0) {
                        tips.push({
                            icon: '⏱️',
                            text: withEffect.length + ' of ' + events.length + ' watering' + (events.length > 1 ? 's' : '') + ' show the soil drying back to its previous level. Check the effect duration per event below.'
                        });
                    }
                    // Check if latest watering did not reach ideal
                    var latest = events[events.length - 1];
                    if (latest.to > 11) {
                        tips.push({
                            icon: '🎯',
                            text: 'Last watering only reached "' + latest.toState + '" (level ' + latest.to + '). For best results, water until the sensor reads 8–11 (Ideal range).'
                        });
                    } else if (latest.to <= 3) {
                        tips.push({
                            icon: '⚠️',
                            text: 'Last watering reached "Very wet" (level ' + latest.to + '). You could extend the time between waterings or reduce the amount.'
                        });
                    }
                    // Suggest next watering time based on avg interval
                    if (avgInterval && events.length >= 2) {
                        tips.push({
                            icon: '📅',
                            text: 'Based on your watering pattern (every ' + avgInterval + '), plan your next watering accordingly.'
                        });
                    }
                }

                return {
                    list: events.reverse(), // newest first
                    count: events.length,
                    avgInterval: avgInterval,
                    tips: tips,
                };
            }
        },
        gardeningInsights() {
            if (!this.isSoilSensor || this.activeDataset.length === 0) return [];
            var current = Number(this.activeDataset[0].value);
            var insights = [];

            // Current level insight
            if (current <= 3) {
                insights.push({ icon: '🌊', title: 'Waterlogged soil', description: 'The soil is saturated. Most plants prefer drier conditions — consider improving drainage or reducing watering.' });
            } else if (current <= 7) {
                insights.push({ icon: '💧', title: 'Well-watered', description: 'Soil moisture is high. Great for tropical plants, ferns, and recently transplanted seedlings.' });
            } else if (current <= 11) {
                insights.push({ icon: '🌱', title: 'Ideal moisture', description: 'This is the sweet spot for most plants. Roots can breathe and absorb nutrients efficiently.' });
            } else if (current <= 15) {
                insights.push({ icon: '🏜️', title: 'Getting dry', description: 'Consider watering soon. Leafy vegetables and flowers may start wilting at this level.' });
            } else {
                insights.push({ icon: '⚠️', title: 'Critically dry', description: 'Soil is very dry. Water as soon as possible to prevent plant stress and root damage.' });
            }

            // Time-based insight
            if (this.activeDataset.length > 0) {
                var lastDate = new Date(this.activeDataset[0].created_at);
                var hour = lastDate.getHours();
                if (hour >= 10 && hour <= 16) {
                    insights.push({ icon: '☀️', title: 'Peak sun hours', description: 'Last reading was during peak sunlight. Evaporation rates are typically highest between 10 AM and 4 PM.' });
                } else if (hour >= 17 || hour <= 6) {
                    insights.push({ icon: '🌙', title: 'Evening/night reading', description: 'Nighttime moisture levels tend to be more stable. This is a good baseline for comparison.' });
                }
            }

            // Trend insight
            if (this.wateringForecast) {
                if (this.wateringForecast.trend === 'drying') {
                    insights.push({ icon: '🍂', title: 'Evaporation active', description: 'The soil is losing moisture. Mulching around plants can slow evaporation and retain water longer.' });
                } else if (this.wateringForecast.trend === 'wetting') {
                    insights.push({ icon: '🌧️', title: 'Moisture increasing', description: 'Water is being absorbed. Avoid overwatering — check if rain is expected before adding more.' });
                } else {
                    insights.push({ icon: '✅', title: 'Balanced environment', description: 'Moisture loss and absorption are balanced. Your soil conditions look healthy.' });
                }
            }

            return insights;
        },
    },

    components: {
        SuitchTrackerStatusWidget,
        SuitchDetailsWidget,
        SuitchDataDetailsWidget,
        SuitchBasicChart,
        SuitchBatteryWidget,
        SuitchTileMap,
    },

    methods: {
        onRangeData(rawData) {
            if (Array.isArray(rawData) && rawData.length > 0) {
                // Normalize: sort by datetime descending (newest first)
                var sorted = rawData.slice().sort(function (a, b) {
                    return new Date(b.created_at) - new Date(a.created_at);
                });
                this.activeDataset = sorted;
                // Fetch location data matching the time range of this dataset
                this.fetchLocationsForDataset(rawData);
            }
        },
        fetchLocationsForDataset(dataset) {
            if (!this.current_device || !dataset || dataset.length === 0) return;
            // Derive date range from the dataset timestamps
            var timestamps = dataset.map(function(d) { return new Date(d.created_at).getTime(); });
            var minTs = Math.min.apply(null, timestamps);
            var maxTs = Math.max.apply(null, timestamps);
            var startDate = new Date(minTs).toISOString();
            var endDate = new Date(maxTs).toISOString();
            this.locationLoading = true;
            this.$store
                .dispatch(GET_FINDMY_LOCATION, {
                    device_token: this.current_device.token,
                    start_date: startDate,
                    end_date: endDate,
                })
                .then(function(data) {
                    this.locationLoading = false;
                    if (data && data.length > 0) {
                        var locs = data.map(function(item) {
                            try {
                                var decoded = JSON.parse(atob(item.value));
                                return {
                                    lat: decoded.lat || 0,
                                    lng: decoded.lon || 0,
                                    created_at: item.created_at,
                                };
                            } catch (e) {
                                return null;
                            }
                        }).filter(function(l) { return l !== null && l.lat !== 0 && l.lng !== 0; });
                        // Sort ascending by time
                        locs.sort(function(a, b) { return new Date(a.created_at) - new Date(b.created_at); });
                        this.sensorLocations = locs;
                    } else {
                        this.sensorLocations = [];
                    }
                }.bind(this))
                .catch(function() {
                    this.locationLoading = false;
                    this.sensorLocations = [];
                }.bind(this));
        },
        fetchLatestLocation() {
            if (!this.current_device) return;
            // Fetch last 24h for initial load (just to get the latest position)
            var now = new Date();
            var dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            this.locationLoading = true;
            this.$store
                .dispatch(GET_FINDMY_LOCATION, {
                    device_token: this.current_device.token,
                    start_date: dayAgo.toISOString(),
                    end_date: now.toISOString(),
                })
                .then(function(data) {
                    this.locationLoading = false;
                    if (data && data.length > 0) {
                        // API returns DESC (newest first), take the first entry
                        var last = data[0];
                        try {
                            var decoded = JSON.parse(atob(last.value));
                            this.sensorLocations = [{
                                lat: decoded.lat || 0,
                                lng: decoded.lon || 0,
                                created_at: last.created_at,
                            }];
                        } catch (e) {
                            this.sensorLocations = [];
                        }
                    }
                }.bind(this))
                .catch(function() {
                    this.locationLoading = false;
                }.bind(this));
        },
        formatHoursEta(hours) {
            if (hours < 0) return null;
            if (hours < 1) return 'Less than 1 hour';
            if (hours < 24) return '~' + Math.round(hours) + ' hours';
            var days = Math.round(hours / 24);
            if (days === 1) return '~1 day';
            return '~' + days + ' days';
        },
        getRelativeTime(dateString) {
            if (!dateString) return null;
            const date = new Date(dateString);
            if (isNaN(date)) return 'N/A';
            const now = new Date();
            const seconds = Math.floor((now - date) / 1000);
            if (seconds < 60) return 'Just now';
            const minutes = Math.floor(seconds / 60);
            if (minutes < 60) return minutes + 'm ago';
            const hours = Math.floor(minutes / 60);
            if (hours < 24) return hours + 'h ago';
            const days = Math.floor(hours / 24);
            return days + 'd ago';
        },
        formatDate(dateString) {
            if (!dateString) return null;
            const options = {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            };
            try {
                const date = new Date(dateString);
                if (isNaN(date)) return null;
                return new Intl.DateTimeFormat(undefined, options).format(date);
            } catch (e) {
                return null;
            }
        },
    },
    mounted() {
        // console.log("route:",this.$route.params);
        const device_token = this.$route.params.token;

        this.$store
            .dispatch(FETCH_SINGLE_DEVICE, { device_token })
            // go to which page after successfully login
            .then((data) => {
                // download devices list
                this.current_device = data;
                if(this.current_device.version == 'templessv1'){
                    this.labelOfData = 'Temperature in °C';
                } else if(this.current_device.version == 'soilblessv1'){
                    this.labelOfData = 'Moisture (0 = Wet → 19 = Dry)';
                }
            });
        this.$store
            .dispatch(GET_FINDMY_SOIL_SENSOR, { device_token: device_token })
            // go to which page after successfully login
            .then((data) => {
                // data is an array of objects with the next shape {value:base64, created_at: date}
                // decode the base64 of value field of each object and store in a local object
                if(data && data.length > 0) {
                    const imarkers = data.map((item) => {
                        return {
                            value: item.value,
                            created_at: item.created_at,
                            showTooltip: false,
                        };
                    });
                    // Normalize: sort by datetime descending (newest first)
                    imarkers.sort(function (a, b) {
                        return new Date(b.created_at) - new Date(a.created_at);
                    });
                    if(this.markers.length == 0){
                        this.limitBatteryDate = imarkers[imarkers.length - 1].created_at;
                    }
                    this.markers = imarkers;
                    this.activeDataset = imarkers;
                    // Fetch initial location
                    this.fetchLatestLocation();
                }
                // Mark data as loaded (even if empty)
                this.dataLoaded = true;
            })
            .catch(() => {
                this.dataLoaded = true;
            });
    },
};
</script>
